import React, { useState, useEffect } from "react";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PhotoCameraBackIcon from "@mui/icons-material/PhotoCameraBack";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";
import styles from "./ImageGalleryV3.module.scss";

/* =====================================================
   MAIN GALLERY COMPONENT
===================================================== */
const ImageGalleryV3 = ({ showGallery, onClose }) => {
  const [activeTab, setActiveTab] = useState("private");
  const [galleryData, setGalleryData] = useState({
    stock_gifs: [],
    stock_images: [],
    private: [],
  });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isGoogleModalOpen, setIsGoogleModalOpen] = useState(false);
  const [gifSearchQuery, setGifSearchQuery] = useState("");
  const [loadingGifSearch, setLoadingGifSearch] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showGifInfoModal, setShowGifInfoModal] = useState(false);

  const fetchGalleryData = async () => {
    try {
      const token = axios.defaults.headers.common.Authorization;
      setLoading(true);
      const { data } = await axios.get("consent_clause/get_user_media", {
        headers: { Authorization: token },
      });
      setGalleryData(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showGallery) fetchGalleryData();
  }, [showGallery]);

  const handleDelete = async (id) => {
    try {
      const token = axios.defaults.headers.common.Authorization;
      await axios.put(
        `/consent_clause/archive_user_media/${id}`,
        {},
        { headers: { Authorization: token } }
      );
      toast.success("Deleted");
      fetchGalleryData();
    } catch {
      toast.error("Delete failed");
    }
  };

  const renderImages = (list) =>
  list.map((img) => (
    <div key={img.id} className={styles.imageCard}>
      <Tooltip 
  title={img.keywords || "No keywords"}
  placement="top"
  classes={{ tooltip: styles.keywordTooltip }}
>
  <img 
    src={img.url} 
    alt={img.name} 
    className={styles.img} 
  />
</Tooltip>

      {/* DURATION BADGE */}
      {img.duration && (
        <div className={styles.gifDurationBadge}>
          {formatDuration(img.duration)}
        </div>
      )}

      {/* CONFIDENTIAL BADGE */}
      {img.is_confidential && (
        <Tooltip title="Confidential">
          <GppMaybeIcon className={styles.confIcon} />
        </Tooltip>
      )}

      {/* DELETE BUTTON */}
      {activeTab === "private" && (
        <IconButton
          className={styles.deleteBtn}
          onClick={() => handleDelete(img.id)}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  ));

const formatDuration = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  return `${hrs.toString().padStart(2, "0")}:${mins
    .toString()
    .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

const fetchStockGifSearch = async (query = "") => {
  try {
    const token = axios.defaults.headers.common.Authorization;
    if (!token) return;

    setLoadingGifSearch(true);

    const response = await axios.get(
      `consent_clause/get_user_media?search=${query}`,
      {
        headers: { Authorization: token },
      }
    );

    // Only replace stock_gifs, keep stock_images & private untouched
    setGalleryData((prev) => ({
      ...prev,
      stock_gifs: response.data.stock_gifs || [],
    }));
  } catch (err) {
    console.error("GIF search failed", err);
  } finally {
    setLoadingGifSearch(false);
  }
};

  if (!showGallery) return null;

  return (
    <div className={styles.panel}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <h2>Image Gallery</h2>
          <div className={styles.tabs}>
            {["private", "stock_gifs"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`${styles.tabBtn} ${
                  activeTab === tab ? styles.activeTab : ""
                }`}
              >
                {tab.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.headerRight}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchGalleryData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Upload new">
            <Button
              variant="contained"
              startIcon={<PhotoCameraBackIcon />}
              onClick={() => setIsUploadModalOpen(true)}
            >
              Upload
            </Button>
          </Tooltip>
          <Tooltip title="Close Gallery">
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </div>
      </header>

{activeTab === "stock_gifs" && (
  <div className={styles.searchRow}>
    <div className={styles.searchContainer}>
      <div className={styles.searchBox}>
        <SearchIcon className={styles.searchIcon} />

        <input
          type="text"
          placeholder="Search GIFs..."
          value={gifSearchQuery}
          onChange={(e) => setGifSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              fetchStockGifSearch(gifSearchQuery);
            }
          }}
        />

        {/* INFO ICON FOR GIF SEARCH */}
        <Tooltip title="How to Search GIFs">
          <IconButton
            className={styles.infoButton}
            size="small"
            onClick={() => setShowGifInfoModal(true)}
          >
            <GppMaybeIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      <Button
        className={styles.searchButton}
        onClick={() => fetchStockGifSearch(gifSearchQuery)}
        disabled={!gifSearchQuery.trim()}
      >
        {loadingGifSearch ? "Searching…" : "Search"}
      </Button>
    </div>
  </div>
)}


      {/* Search bar */}
  {activeTab === "private" && (
  <div className={styles.searchRow}>
    <div className={styles.searchContainer}>
      <div className={styles.searchBox}>
        <SearchIcon className={styles.searchIcon} />

        <input
          type="text"
          placeholder="Search Google Images..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        {/* GOOGLE LOGO */}
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg"
          alt="Google"
          className={styles.googleIcon}
        />

        {/* NEW INFO ICON */}
        <Tooltip title="About Google Image Search">
          <IconButton
            className={styles.infoButton}
            onClick={() => setShowInfoModal(true)}
            size="small"
          >
            <GppMaybeIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      <Button
        className={styles.searchButton}
        onClick={() => setIsGoogleModalOpen(true)}
        disabled={!searchQuery.trim()}
      >
        Search
      </Button>
    </div>
  </div>
)}

{/* INFO MODAL */}
{showInfoModal && (
  <Modal open={showInfoModal} onClose={() => setShowInfoModal(false)}>
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 500,
        bgcolor: "white",
        p: 3,
        borderRadius: 2,
        boxShadow: 24,
      }}
    >
      <Typography variant="h6" fontWeight={600} mb={2}>
        About Google Image Search
      </Typography>

      <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.5 }}>
        This tool lets you search images on the web using Google Image Search.
        When you choose an image, it will be automatically imported into your
        <strong> private gallery</strong> inside this system.
        <br /><br />
        These images are free-to-use, non-copyright restricted,
        and can safely be added inside consent forms or treatment documents.
      </Typography>

      <Typography variant="body2" sx={{ opacity: 0.7, mb: 3 }}>
        Note: Only JPG and PNG images are supported. GIFs are allowed only
        inside the stock GIF library.
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button variant="contained" onClick={() => setShowInfoModal(false)}>
          OK, Got It
        </Button>
      </Box>
    </Box>
  </Modal>
)}

 {/* GIF INFO MODAL */}
{showGifInfoModal && (
  <Modal open={showGifInfoModal} onClose={() => setShowGifInfoModal(false)}>
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 500,
        bgcolor: "white",
        p: 3,
        borderRadius: 2,
        boxShadow: 24,
      }}
    >
      <Typography variant="h6" fontWeight={600} mb={2}>
        How to Search Stock GIFs
      </Typography>

      <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 2 }}>
        The Stock GIF Library contains ready-made animated GIFs for use in your
        consent templates. You can search them using keywords related to
        <strong> dental procedures, conditions, treatments, and symptoms.</strong>
        <br /><br />
        Use clear and simple search terms for best results.
      </Typography>

      <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
        🔍 What to Search
      </Typography>
      <ul style={{ lineHeight: 1.6, marginTop: 4 }}>
        <li>Specific procedures (e.g., "root canal", "crown prep")</li>
        <li>Dental conditions (e.g., "erosion", "gum disease")</li>
        <li>Symptoms (e.g., "tooth pain")</li>
        <li>Movements/processes (e.g., "brushing", "flossing")</li>
      </ul>

      <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
        🧪 Example Searches
      </Typography>
      <ul style={{ lineHeight: 1.6, marginTop: 4 }}>
        <li>"tooth wear gif"</li>
        <li>"crown preparation animation"</li>
        <li>"gingivitis animation"</li>
        <li>"tooth decay progression gif"</li>
        <li>"braces tightening animation"</li>
      </ul>

      <Typography variant="body2" sx={{ opacity: 0.7, mt: 2 }}>
        Tips:  
        <br />• Use short keywords  
        <br />• Avoid long phrases or sentences  
        <br />• Only GIF results will appear  
        <br />• High-quality medical animations work best  
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button variant="contained" onClick={() => setShowGifInfoModal(false)}>
          Got It
        </Button>
      </Box>
    </Box>
  </Modal>
)}




      {/* Gallery grid */}
      {loading ? (
        <div className={styles.loader}>
          <CircularProgress />
          <p>Loading images...</p>
        </div>
      ) : (
        <div className={styles.grid}>
          {renderImages(galleryData[activeTab] || [])}
        </div>
      )}

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <ImageUploadModal
          open={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          fetchGalleryData={fetchGalleryData}
        />
      )}

      {/* Google Modal */}
      {isGoogleModalOpen && (
        <GoogleSearchModal
          open={isGoogleModalOpen}
          onClose={() => setIsGoogleModalOpen(false)}
          searchQuery={searchQuery}
          fetchGalleryData={fetchGalleryData}
        />
      )}
    </div>
  );
};

/* =====================================================
   GOOGLE IMAGE SEARCH MODAL (Multi-select + Pagination)
===================================================== */
const GoogleSearchModal = ({
  open,
  onClose,
  searchQuery: initialQuery,
  fetchGalleryData,
}) => {
  const apiKey = "AIzaSyDBGKDuYYvI1DRbCjvrtnCRasnGukhYOes";
  const cx = "30dca0bba2d8b4b27";
  const numResults = 10;

  const [searchQuery, setSearchQuery] = useState(initialQuery || "");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [selectedImages, setSelectedImages] = useState([]);

  const fetchResults = async (pageNum = 1, query = searchQuery) => {
    if (!query.trim()) return toast.warn("Enter a search term.");
    setLoading(true);
    try {
      const startIndex = (pageNum - 1) * numResults + 1;
      const apiUrl = `https://www.googleapis.com/customsearch/v1?q=${encodeURIComponent(
        query
      )}&cx=${cx}&searchType=image&key=${apiKey}&num=${numResults}&start=${startIndex}`;
      const res = await fetch(apiUrl);
      const data = await res.json();
      const results = (data.items || []).filter((i) =>
        /\.(jpg|jpeg|png)$/i.test(i.link)
      );
      setSearchResults(results);
      setTotalResults(parseInt(data.searchInformation?.totalResults || "0"));
    } catch {
      toast.error("Failed to fetch images.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && searchQuery) fetchResults(page, searchQuery);
  }, [open, page]);

  const toggleSelectImage = (url) =>
    setSelectedImages((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]
    );

  const handleUploadSelected = async () => {
    if (!selectedImages.length)
      return toast.error("Please select at least one image.");
    const token = axios.defaults.headers.common.Authorization;
    setLoading(true);
    try {
      for (const url of selectedImages) {
        const formData = new FormData();
        formData.append("image_url", url);
        await axios.post("consent_clause/upload_google_image_to_s3", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: token,
          },
        });
      }
      toast.success(`${selectedImages.length} image(s) uploaded!`);
      await fetchGalleryData();
      setSelectedImages([]);
      onClose();
    } catch {
      toast.error("Failed to upload images.");
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalResults / numResults) || 1;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          bgcolor: "#fafafa",
          borderRadius: "16px",
          boxShadow:
            "0 4px 8px rgba(0,0,0,0.1), 0 12px 24px rgba(0,0,0,0.15)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            p: 2,
            borderBottom: "1px solid #ddd",
            background: "#fff",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <img
              src="https://www.gstatic.com/images/branding/product/1x/googlelogo_light_color_42dp.png"
              alt="Google"
              style={{ height: 28 }}
            />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Image Search
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Search Bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 2,
            gap: 2,
            background: "#f9fafb",
            borderBottom: "1px solid #eee",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              background: "#fff",
              border: "1px solid #d0d5dd",
              borderRadius: "10px",
              px: 2,
            }}
          >
            <SearchIcon sx={{ color: "#9ca3af", mr: 1 }} />
            <input
              type="text"
              placeholder="Search Google Images..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchResults(1, searchQuery)}
              style={{
                border: "none",
                outline: "none",
                flex: 1,
                fontSize: "15px",
                background: "transparent",
              }}
            />
          </Box>
          <Button variant="contained" onClick={() => fetchResults(1, searchQuery)}>
            Search
          </Button>
        </Box>

        {/* Image Grid */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 3, background: "#fafafa" }}>
          {loading ? (
            <Box sx={{ textAlign: "center", py: 10 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Fetching images...</Typography>
            </Box>
          ) : searchResults.length === 0 ? (
            <Typography sx={{ textAlign: "center", py: 6, color: "#999" }}>
              No images found.
            </Typography>
          ) : (
            <div style={{ columnCount: 5, columnGap: "12px" }}>
              {searchResults.map((item) => {
                const selected = selectedImages.includes(item.link);
                return (
                  <div
                    key={item.link}
                    onClick={() => toggleSelectImage(item.link)}
                    style={{
                      position: "relative",
                      marginBottom: "12px",
                      cursor: "pointer",
                      borderRadius: "10px",
                      overflow: "hidden",
                      boxShadow: selected
                        ? "0 0 0 3px #2563eb"
                        : "0 1px 3px rgba(0,0,0,0.1)",
                      transform: selected ? "scale(1.03)" : "scale(1)",
                      transition: "0.2s ease",
                    }}
                  >
                    <img
                      src={item.link}
                      alt={item.title}
                      style={{
                        width: "100%",
                        height: "auto",
                        filter: selected ? "brightness(0.85)" : "none",
                      }}
                    />
                    {selected && (
                      <div
                        style={{
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                          background: "#2563eb",
                          color: "#fff",
                          borderRadius: "50%",
                          width: "24px",
                          height: "24px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "14px",
                        }}
                      >
                        ✓
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Box>

        {/* Pagination + Upload Footer */}
        <Box
          sx={{
            p: 2,
            background: "#fff",
            borderTop: "1px solid #eee",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              variant="outlined"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Typography>
              Page {page} / {Math.max(1, totalPages)}
            </Typography>
            <Button
              variant="outlined"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </Box>
          <Button
            variant="contained"
            color="primary"
            disabled={!selectedImages.length || loading}
            onClick={handleUploadSelected}
          >
            {loading ? "Saving..." : `Save (${selectedImages.length})`}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

/* =====================================================
   LOCAL UPLOAD MODAL
===================================================== */
const ImageUploadModal = ({ open, onClose, fetchGalleryData }) => {
  const [uploadedImages, setUploadedImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const { getRootProps, getInputProps } = useDropzone({
    accept: { "image/*": [] },
    multiple: false,
    onDrop: (files) => setUploadedImages(files),
  });

  const handleImageUpload = async () => {
    if (!uploadedImages.length) return toast.error("Select an image first.");
    const token = axios.defaults.headers.common.Authorization;
    setLoading(true);
    try {
      for (const file of uploadedImages) {
        const formData = new FormData();
        formData.append("file", file);
        await axios.post("consent_clause/upload_user_media_to_s3", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: token,
          },
        });
      }
      toast.success("Uploaded successfully.");
      await fetchGalleryData();
      onClose();
    } catch {
      toast.error("Upload failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 900,
          bgcolor: "white",
          p: 3,
          borderRadius: 2,
          boxShadow: 24,
        }}
      >
        <Typography variant="h6" mb={2}>
          Upload Image
        </Typography>
        <div
          {...getRootProps()}
          style={{
            border: "2px dashed #ccc",
            padding: "50px",
            borderRadius: "10px",
            textAlign: "center",
            cursor: "pointer",
          }}
        >
          <input {...getInputProps()} />
          <p>Drag & drop or click to select an image</p>
        </div>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleImageUpload}
            disabled={loading || !uploadedImages.length}
          >
            {loading ? "Uploading..." : "Upload"}
          </Button>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ImageGalleryV3;
